// §11.5 — the Store.
//
// "Cards with icon, name, description, permissions requested. **Install** pins to launcher + bar;
// **Uninstall** removes and drops cached data after a confirm. Paste-a-URL install is present but
// disabled for guests and labelled *'Available when signed in'* (D-024, D-025)."
//
// The card shows the permissions **before** the install, which is the only moment where showing
// them means anything: §9.4 grants a catalog app its defaults without a prompt, so this is where
// a person finds out what they are agreeing to.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sectionsOf } from '@hangar/shared';
import type { HangarAPI, ManifestProblem, StoreCard, StoreShellAccess } from '@hangar/shared';
import { COPY } from '@/copy';

function Card({
  card,
  busy,
  onInstall,
  onUninstall,
}: {
  card: StoreCard;
  busy: boolean;
  onInstall: () => void;
  onUninstall: () => void;
}) {
  return (
    <article className="store-card" data-testid={`store-card-${card.id}`}>
      <span
        className="store-icon"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: card.icon }}
      />
      <div className="store-text">
        <h3>{card.name}</h3>
        <p className="store-description">{card.description}</p>
        <p className="store-origin">{card.origin}</p>

        {/* §9.4 — what it asks for, before it has it. */}
        <div className="store-perms" data-testid={`store-perms-${card.id}`}>
          {card.permissions.length === 0 ? (
            <span className="store-no-perms">No permissions requested</span>
          ) : (
            card.permissions.map((permission) => (
              <span key={permission} className="store-perm">
                {permission}
              </span>
            ))
          )}
        </div>

        {card.problems.length > 0 && (
          <ul className="store-problems" data-testid={`store-problems-${card.id}`}>
            {card.problems.map((problem: ManifestProblem) => (
              <li key={`${problem.path}:${problem.message}`}>
                <code>{problem.path}</code> {problem.message}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="store-actions">
        {card.installed ? (
          <>
            <span className="store-state" data-testid={`store-state-${card.id}`}>
              {card.unhealthy ? 'Not responding' : 'Installed'}
            </span>
            <button
              type="button"
              className="store-uninstall"
              data-testid={`store-uninstall-${card.id}`}
              onClick={onUninstall}
            >
              {COPY.appUninstall}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="store-install"
            data-testid={`store-install-${card.id}`}
            disabled={busy}
            onClick={onInstall}
          >
            {busy ? 'Installing…' : 'Install'}
          </button>
        )}
      </div>
    </article>
  );
}

export function Store({ access }: { api: HangarAPI; access: StoreShellAccess }) {
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');

  useEffect(() => access.subscribe(() => setTick((n) => n + 1)), [access]);

  const cards = useMemo(() => {
    void tick;
    return access.cards();
  }, [access, tick]);

  const canPaste = access.canPasteUrl();

  const install = useCallback(
    (appId: string) => {
      setBusy(appId);
      void access
        .install(appId)
        .then(() => setTick((n) => n + 1))
        .finally(() => setBusy(null));
    },
    [access],
  );

  const uninstall = useCallback(
    (appId: string) => {
      void access.uninstall(appId).then(() => setTick((n) => n + 1));
    },
    [access],
  );

  // §29.1 (D-369) — categories, and inside the Arcade its four sub-group headers. `sectionsOf`
  // is the kernel's ordering, so the Store and the three launchers cannot disagree about which
  // header a title sits under or what order the headers come in.
  const sections = useMemo(() => sectionsOf(cards), [cards]);

  return (
    <div className="store" data-testid="store">
      <header className="store-head">
        <h2>Store</h2>
        {/* D-025 — present, disabled, and labelled, so the deferral is visible rather than absent. */}
        <div className="store-paste">
          <input
            type="url"
            data-testid="store-paste-url"
            placeholder="https://app.example.com"
            aria-label="Install from a URL"
            disabled={!canPaste}
            value={pasted}
            onChange={(event) => setPasted(event.target.value)}
          />
          <button
            type="button"
            data-testid="store-paste-install"
            disabled={!canPaste || pasted.trim().length === 0}
            onClick={() => {
              void access.installFromUrl(pasted).then(() => setTick((n) => n + 1));
            }}
          >
            Install
          </button>
          {!canPaste && (
            <span className="store-paste-note" data-testid="store-paste-note">
              {COPY.storePasteUrlGuest}
            </span>
          )}
        </div>
      </header>

      <div className="store-body">
        {sections.map((section) => (
          <section
            key={`${section.category ?? ''}/${section.group ?? ''}`}
            className="store-group"
            data-testid={`store-section-${section.group ?? section.category ?? 'apps'}`}
          >
            <h3 className="store-group-name">
              {section.group === null ? section.category : `${section.category} · ${section.group}`}
            </h3>
            {section.apps.map((card) => (
              <Card
                key={card.id}
                card={card}
                busy={busy === card.id}
                onInstall={() => install(card.id)}
                onUninstall={() => uninstall(card.id)}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
